import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles, withStyles, Typography, Button } from '@material-ui/core';
import {
  DEFAULT_BACK_TO_URL_MOBILEAPP,
  DEFAULT_BACK_TO_LABEL_MOBILEAPP,
  STATIC_PATH,
  STATUS_CODES,
} from 'constants';
import {
  getLocalSettingsProxy,
  checkIsMobileApp,
  locationLogic,
} from 'utilities';

const useStyles = makeStyles((theme) => {
  return {
    statusPageContainer: {
      backgroundColor: '#F9FAFC',
      height: '100vh',
      padding: theme.spacing(3),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(17.25),
      },
    },
    statusContainer: {
      display: 'flex',
      padding: theme.spacing(0, 3),
      background: ({ forbiddenPattern }) =>
        !forbiddenPattern ? theme.palette.common.white : '#F9FAFC',
      border: ({ forbiddenPattern }) =>
        !forbiddenPattern ? `1px solid ${theme.palette.grey?.[300]}` : '',
      width: '100%',
      height: '100%',
      maxWidth: theme.spacing(145.5),
      overflow: 'hidden',

      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0, 12),
      },

      [theme.breakpoints.up('md')]: {
        minHeight: theme.spacing(41.5),
        height: 'unset',
      },

      [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(0, 24),
      },
    },
    status: {
      display: 'flex',
      width: '100%',
      padding: theme.spacing(9, 0),
      flexDirection: 'column',
      justifyContent: 'center',
    },
    imageContainer: {
      position: 'relative',
      width: theme.spacing(23.25),
    },
    owliverImg: {
      position: 'absolute',
      bottom: 0,
      right: `-${theme.spacing(3.25)}px`,
      pointerEvents: 'none',

      [theme.breakpoints.up('md')]: {
        right: 'unset',
      },
    },
    whoops: {
      paddingBottom: theme.spacing(2),
    },
    title: {
      color: ({ forbiddenPattern }) =>
        !forbiddenPattern ? theme.palette.greyBlue?.[700] : '',
      paddingBottom: theme.spacing(2),
    },
    message: {
      color: ({ forbiddenPattern }) =>
        !forbiddenPattern ? theme.palette.greyBlue?.[700] : '',
    },
    left_button: {
      marginTop: theme.spacing(4.5),
    },
    right_button: {
      marginTop: theme.spacing(4.5),
      alignSelf: 'flex-end',
    },
  };
});

const StyledButton = withStyles((theme) => ({
  root: {
    backgroundColor: '#0071DB',
    color: theme.palette.common.white,
    letterSpacing: '0.46px',
    lineHeight: '26px',
    padding: theme.spacing(1, 2.619),
    width: 'fit-content',
    '&:hover': {
      backgroundColor: '#003D75',
    },
    '&:focus-visible': {
      boxShadow: null,
      outlineColor: '#003D75',
      outlineOffset: 2,
      outlineStyle: 'solid',
      outlineWidth: 2,
    },
  },
}))(Button);

const createStatusPageContent = (statusCode) => {
  let title, message;
  let buttonLabel = 'Back To Shelf';
  let buttonToRight = true;
  let showOwliver = false;

  if (statusCode === STATUS_CODES.UNAUTHORIZED) {
    title = 'Your session timed out.';
    message =
      'Your session expired due to inactivity. To continue reading, please log back in and relaunch your book.';
    buttonLabel = 'Login';
    buttonToRight = false;
    showOwliver = true;
  } else if (statusCode === STATUS_CODES.NOT_FOUND) {
    title = 'Page Not Found';
    message = "Sorry, we couldn't find this page.";
  } else if (statusCode === STATUS_CODES.INTERNAL_SERVER_ERROR) {
    title = 'An error has occurred.';
    message =
      'Internal server error has occurred and was unable to complete your request.';
  } else if (statusCode === STATUS_CODES.FORBIDDEN) {
    title = `You've hit your maximum session allowance.`;
    message =
      'You may only have three concurrent sessions open. Please close one active session to continue.';
    buttonLabel = 'Back To My Shelf';
    buttonToRight = false;
  } else {
    title = 'Application Error';
    message =
      'Oops, something went wrong.  The application has encountered an error.  Try going back to shelf to continue.';
  }

  return { title, message, buttonLabel, buttonToRight, showOwliver };
};

const StatusPage = ({ statusCode, bookPk, ...props }) => {
  const forbiddenPattern = statusCode === STATUS_CODES.FORBIDDEN;

  const classes = useStyles({ forbiddenPattern });
  const [localSettings] = useState(
    bookPk ? getLocalSettingsProxy(bookPk) : null,
  );

  const isMobileApp = checkIsMobileApp(
    locationLogic.getSearchStringFromLocation(window.location),
  );

  const backToLabel = isMobileApp
    ? (localSettings?.backToLabel?.trim() ?? DEFAULT_BACK_TO_LABEL_MOBILEAPP)
    : localSettings?.backToLabel?.trim();
  const backToUrl = isMobileApp
    ? (localSettings?.backToUrl?.trim() ?? DEFAULT_BACK_TO_URL_MOBILEAPP)
    : localSettings?.backToUrl?.trim();

  const shouldDisplayButton = Boolean(backToLabel) && Boolean(backToUrl);

  const { title, message, buttonLabel, buttonToRight, showOwliver } =
    createStatusPageContent(statusCode);

  return (
    <div
      className={clsx(classes.statusPageContainer, 'status-page')}
      {...props}
    >
      <div className={classes.statusContainer}>
        <div className={classes.status}>
          {forbiddenPattern && (
            <Typography className={classes.whoops} variant="h1">
              Whoops!
            </Typography>
          )}
          <Typography
            className={classes.title}
            variant={forbiddenPattern ? 'h2' : 'h3'}
          >
            {title}
          </Typography>
          <Typography
            className={classes.message}
            variant={forbiddenPattern ? 'h5' : 'body1'}
          >
            {message}
          </Typography>
          {shouldDisplayButton && (
            <StyledButton
              aria-label={backToLabel}
              className={
                buttonToRight ? classes.right_button : classes.left_button
              }
              color="primary"
              component="a"
              disableElevation
              href={backToUrl}
              variant="contained"
            >
              {buttonLabel}
            </StyledButton>
          )}
        </div>
        {showOwliver && (
          <div className={classes.imageContainer}>
            <img
              alt=""
              src={`${STATIC_PATH}/images/owl.svg`}
              className={classes.owliverImg}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusPage;
